import { CircleCheck, FileCog, FileText, Loader2, X } from '@/components/icons'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/lib/documents'
import type { FileUploadProgress } from '@/lib/hooks/useDocumentFileUpload'

interface DocumentFileCardProps {
  // Newly selected file (create mode, or a replacement in edit mode)
  file?: File | null
  // Current file on the document (edit mode, nothing selected yet)
  existingFileName?: string | null
  progress: FileUploadProgress | null
  // id of the hidden <input type="file"> the Replace label triggers
  replaceInputId: string
  onRemove: () => void
  disabled?: boolean
}

// The file section card shared by every document drawer: a selected file with
// upload progress/state, or the document's current file with a Replace action.
export function DocumentFileCard({
  file,
  existingFileName,
  progress,
  replaceInputId,
  onRemove,
  disabled,
}: DocumentFileCardProps) {
  if (file) {
    return (
      <div className="bg-surface-L2 rounded-[10px] p-4 relative overflow-hidden">
        {progress && progress.status === 'uploading' && (
          <div
            className="absolute inset-0 bg-background-brand-bold/10 transition-all duration-300"
            style={{ width: `${progress.progress}%` }}
          />
        )}

        <div className="flex items-center gap-3 relative">
          {progress?.status === 'completed' ? (
            <CircleCheck className="w-6 h-6 text-content-positive-default shrink-0" />
          ) : progress?.status === 'uploading' ? (
            <Loader2 className="w-6 h-6 text-content-primary-subtle shrink-0 animate-spin" />
          ) : (
            <FileText className="w-6 h-6 text-content-primary-subtle shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-content-primary-bold truncate">{file.name}</p>
            <p className="text-sm text-content-primary-subtle">
              {progress?.status === 'uploading'
                ? `Uploading... ${progress.progress}%`
                : progress?.status === 'completed'
                  ? 'Completed'
                  : progress?.status === 'error'
                    ? progress.error || 'Error'
                    : formatFileSize(file.size)}
            </p>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="p-1 hover:bg-content-primary-subtle/10 rounded transition-colors"
            >
              <X className="w-4 h-4 text-content-primary-subtle" />
            </button>
          )}
        </div>
      </div>
    )
  }

  if (existingFileName) {
    return (
      <div className="bg-surface-L2 rounded-[10px] p-4">
        <div className="flex items-center gap-3">
          <FileCog className="w-6 h-6 text-content-primary-bold shrink-0" />
          <p className="flex-1 text-sm font-medium text-content-primary-bold tracking-[0.07px] truncate">
            {existingFileName}
          </p>
          <label
            htmlFor={replaceInputId}
            className={cn(
              'text-sm font-medium text-content-primary-bold cursor-pointer hover:underline',
              disabled && 'opacity-50 pointer-events-none'
            )}
          >
            Replace
          </label>
        </div>
      </div>
    )
  }

  return null
}
